import { SystemStyleObject, useColorModeValue } from '@chakra-ui/react';
import { ChakraStylesConfig, CreatableSelect, Select as RSelect } from 'chakra-react-select';
import { useState } from 'react';

import { ReactSelectProps } from '../utils/types';

type StyleFn = (provided: SystemStyleObject, state: any) => SystemStyleObject;

// Compose caller overrides on top of our defaults per slot, so tweaking one slot
// (e.g. the value container's padding) doesn't drop the rest of the styling.
const mergeChakraStyles = (base: ChakraStylesConfig, overrides?: ChakraStylesConfig): ChakraStylesConfig => {
  if (!overrides) return base;

  const merged: Record<string, StyleFn> = {};
  const keys = new Set([...Object.keys(base), ...Object.keys(overrides)]);

  keys.forEach((key) => {
    const baseStyle = (base as Record<string, StyleFn>)[key];
    const overrideStyle = (overrides as Record<string, StyleFn>)[key];

    if (!baseStyle) merged[key] = overrideStyle;
    else if (!overrideStyle) merged[key] = baseStyle;
    else merged[key] = (provided, state) => overrideStyle(baseStyle(provided, state), state);
  });

  return merged as ChakraStylesConfig;
};

export const ReactSelect = ({
  value: controlledValue,
  onChange: controlledOnChange,
  onCreateOption,
  name,
  max,
  options,
  defaultValue,
  type,
  showDropdownIndicator = true,
  isMulti = false,
  isInvalid = false,
  chakraStyles: chakraStylesOverrides,
  ...rest
}: ReactSelectProps) => {
  const [value, setValue] = useState(defaultValue);

  const defaultOption = defaultValue ? { value: defaultValue, label: defaultValue } : '';
  const bgColor = useColorModeValue('transparent', 'whiteAlpha.50');
  const bgColorHover = useColorModeValue('transparent', 'whiteAlpha.100');
  const tagVariant = useColorModeValue('solid', 'subtle');

  const baseChakraStyles: ChakraStylesConfig = {
    control: (provided: SystemStyleObject) => ({
      ...provided,
      border: 0,
      // The wrapping FormControl draws the field's border; this is a fallback so the
      // error state is still visible if that border is ever hidden or overridden.
      ...(isInvalid && { boxShadow: 'inset 0 0 0 1px var(--chakra-colors-error)' }),
    }),
    dropdownIndicator: (provided: SystemStyleObject) => ({
      ...provided,
      bgColor,
      px: 2,
      _hover: { bgColor: bgColorHover },
      display: showDropdownIndicator ? 'flex' : 'none',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    valueContainer: (provided: SystemStyleObject, { hasValue, isMulti }) => ({
      ...provided,
      bgColor,
      px: hasValue && isMulti ? 2 : 4,
      py: 2,
      _hover: { bgColor: bgColorHover },
    }),
    option: (provided: SystemStyleObject) => ({
      ...provided,
      pl: 4,
    }),
    group: () => ({ pb: 4, pl: 4 }),
    groupHeading: (provided: SystemStyleObject) => ({
      ...provided,
      p: 4,
      ml: -4,
      fontSize: 16,
      fontWeight: 'bold',
      textTransform: 'uppercase',
      borderWidth: 0,
    }),
    menuList: (provided: SystemStyleObject) => ({
      ...provided,
      width: '200px',
    }),
  };

  const chakraStyles = mergeChakraStyles(baseChakraStyles, chakraStylesOverrides);

  const finalValue: any = controlledValue || value || '';

  const finalSetValue = controlledOnChange || setValue;

  return (
    <>
      {type === 'creatable' ? (
        <CreatableSelect
          chakraStyles={chakraStyles}
          tagVariant={tagVariant}
          size="sm"
          isClearable={false}
          isMulti={isMulti}
          isInvalid={isInvalid}
          name={name}
          onChange={finalSetValue}
          value={finalValue}
          options={options}
          onCreateOption={onCreateOption}
          defaultValue={defaultOption}
          {...rest}
        />
      ) : (
        <RSelect
          chakraStyles={chakraStyles}
          tagVariant={tagVariant}
          size="sm"
          isClearable={false}
          isMulti={isMulti}
          isInvalid={isInvalid}
          onChange={finalSetValue}
          value={finalValue}
          options={options}
          isOptionDisabled={() => !!max && finalValue?.length >= max}
          defaultValue={defaultOption}
          {...rest}
        />
      )}
    </>
  );
};
